/* ==================================================================
   ACRAEA — scroll-driven ground, and the drawer.

   The ground is a page-level colour, not a per-section one. Sections no
   longer paint themselves; they only DECLARE which scheme they want, and
   whichever section owns the middle of the viewport hands its scheme to
   :root. Everything reading those tokens — the header included — drifts
   with it, because the transition lives in CSS rather than in a loop here.
   ================================================================== */
(function () {
  "use strict";

  var ACRAEA = (window.ACRAEA = window.ACRAEA || {});

  /* ---- ground ---------------------------------------------------- */

  var root = document.documentElement;

  // Every section already labels itself `scheme-<name>` for its own colours,
  // so the ground reads that rather than asking each template for a second,
  // duplicate attribute. The hero carries photography instead of a scheme;
  // it is always the dark end.
  var zones = [].slice
    .call(document.querySelectorAll('[class*="scheme-"], .hero'))
    .map(function (el) {
      var named = (el.className.match(/scheme-([a-z]+)/) || [])[1];
      el.dataset.scheme = named || "ink";
      return el;
    });

  // Which zone owns the ground: the one the viewport's midline is standing
  // in. Exported so the choice can be tested on plain numbers, without a
  // browser — it is the only part of this file with a wrong answer.
  //
  // Deliberately NOT an IntersectionObserver. A thin observer band gets
  // skipped whole by fast wheels, anchor jumps and restored scroll
  // positions, and the ground then stays on a colour the reader has
  // already scrolled past. Recomputing from absolute geometry cannot
  // drift: every frame re-derives the answer from scratch.
  function pickIndex(bounds, midY) {
    var chosen = 0;
    for (var i = 0; i < bounds.length; i++) {
      if (bounds[i].top <= midY) chosen = i;
      else break;
    }
    // Past the end of the last zone — the footer's own scheme still holds.
    return chosen;
  }
  ACRAEA.pickIndex = pickIndex;

  if (zones.length) {
    var current = null;

    function wear(scheme) {
      if (scheme === current) return;
      current = scheme;
      root.setAttribute("data-ground", scheme);

      // Keep the browser chrome in step with the ground, so the notch and
      // the address bar do not sit at yesterday's colour.
      var meta = document.querySelector('meta[name="theme-color"]');
      if (meta) {
        var painted = getComputedStyle(document.body).backgroundColor;
        if (painted) meta.setAttribute("content", painted);
      }
    }

    var ticking = false;

    function settle() {
      ticking = false;
      var midY = window.scrollY + window.innerHeight / 2;
      var bounds = zones.map(function (zone) {
        return { top: zone.getBoundingClientRect().top + window.scrollY };
      });
      wear(zones[pickIndex(bounds, midY)].dataset.scheme);
    }

    function schedule() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(settle);
    }

    settle();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    // Images landing late move every boundary below them.
    window.addEventListener("load", schedule);
  }

  /* ---- header, thinned once you leave the top --------------------- */

  var header = document.querySelector(".header");
  if (header) {
    var lift = function () {
      header.classList.toggle("header--lifted", window.scrollY > 24);
    };
    lift();
    window.addEventListener("scroll", lift, { passive: true });
  }

  /* ---- product gallery, and the lightbox over it ------------------ */

  // NOTE: this block sits ABOVE the drawer on purpose. The drawer bails with
  // an early return when its element is missing, and anything written after
  // that never runs on a page without one.

  var gallery = document.querySelector("[data-gallery]");
  var lightbox = document.getElementById("lightbox");

  if (gallery && lightbox) {
    var shots = [].slice.call(gallery.querySelectorAll(".pdp__shot"));
    var stage = lightbox.querySelector("#lightbox-img");
    var counter = lightbox.querySelector("[data-lb-i]");
    var total = lightbox.querySelector("[data-lb-n]");
    var at = 0;
    var opener = null;

    total.textContent = shots.length;
    if (shots.length < 2) lightbox.classList.add("is-single");

    function show(i) {
      // Wrap in both directions, so the arrows never dead-end.
      at = (i + shots.length) % shots.length;
      var shot = shots[at];
      var thumb = shot.querySelector("img");
      stage.src = shot.dataset.full || thumb.src;
      stage.alt = thumb.alt || "";
      // Carry the real ratio across so a landscape shot is not reserved a
      // portrait box while it loads.
      if (thumb.naturalWidth) {
        stage.width = thumb.naturalWidth;
        stage.height = thumb.naturalHeight;
      }
      counter.textContent = at + 1;
    }

    function openAt(i) {
      opener = document.activeElement;
      show(i);
      lightbox.hidden = false;
      // Force layout before adding the class, so the fade has a start value.
      void lightbox.offsetWidth;
      lightbox.classList.add("is-open");
      document.body.classList.add("no-scroll");
      lightbox.querySelector("[data-lb-close]").focus();
    }

    function closeLightbox() {
      lightbox.classList.remove("is-open");
      document.body.classList.remove("no-scroll");
      if (opener) opener.focus();
      setTimeout(function () {
        if (!lightbox.classList.contains("is-open")) lightbox.hidden = true;
      }, 260);
    }

    shots.forEach(function (shot, i) {
      shot.addEventListener("click", function () {
        openAt(i);
      });
    });

    lightbox.querySelector("[data-lb-close]").addEventListener("click", closeLightbox);
    lightbox.querySelector("[data-lb-prev]").addEventListener("click", function () {
      show(at - 1);
    });
    lightbox.querySelector("[data-lb-next]").addEventListener("click", function () {
      show(at + 1);
    });

    // Clicking the scrim closes; clicking the photograph itself does not.
    lightbox.addEventListener("click", function (e) {
      if (e.target === lightbox || e.target.classList.contains("lightbox__stage")) {
        closeLightbox();
      }
    });

    document.addEventListener("keydown", function (e) {
      if (lightbox.hidden) return;
      if (e.key === "Escape") return closeLightbox();
      if (e.key === "ArrowLeft") return show(at - 1);
      if (e.key === "ArrowRight") return show(at + 1);
    });

    // Swipe. Horizontal only, and only past a threshold, so a vertical scroll
    // or a shaky tap is never read as a page turn.
    var startX = 0;
    var startY = 0;
    var tracking = false;

    lightbox.addEventListener(
      "touchstart",
      function (e) {
        if (e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = true;
      },
      { passive: true }
    );

    lightbox.addEventListener(
      "touchend",
      function (e) {
        if (!tracking) return;
        tracking = false;
        var touch = e.changedTouches[0];
        var dx = touch.clientX - startX;
        var dy = touch.clientY - startY;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
        show(dx < 0 ? at + 1 : at - 1);
      },
      { passive: true }
    );
  }

  /* ---- carry a Reading into the order ----------------------------- */

  // Reading recommendations arrive from another host. Keep the result as
  // private line-item properties so the order can be traced in Shopify
  // without placing quiz language in front of the customer at checkout.
  var readingFields = document.querySelectorAll("[data-reading-property]");
  if (readingFields.length) {
    var query = new URLSearchParams(window.location.search);
    var fromReading =
      query.get("source") === "reading" ||
      query.has("reading") ||
      query.has("practice") ||
      query.has("recommendation");

    if (fromReading) {
      readingFields.forEach(function (field) {
        var key = field.dataset.readingProperty;
        var value = key === "source" ? query.get(key) || "reading" : query.get(key);
        if (!value) return;
        field.value = value;
        field.disabled = false;
      });
    }
  }

  /* ---- drawer ----------------------------------------------------- */

  var drawer = document.getElementById("drawer");
  if (!drawer) return;

  var openers = document.querySelectorAll("[data-drawer-open]");
  var closers = drawer.querySelectorAll("[data-drawer-close]");
  var lastFocused = null;

  function focusables() {
    return [].slice
      .call(
        drawer.querySelectorAll(
          'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
        )
      )
      .filter(function (el) {
        return el.offsetParent !== null;
      });
  }

  function open() {
    lastFocused = document.activeElement;
    drawer.hidden = false;
    // Force layout between unhiding and animating, so the transition has a
    // start value to move from. Synchronous on purpose: a requestAnimationFrame
    // here does not run in a throttled or backgrounded tab, and the drawer
    // would then "open" invisibly while still locking the page behind it.
    void drawer.offsetWidth;
    drawer.classList.add("is-open");
    document.body.classList.add("no-scroll");
    var first = focusables()[0];
    if (first) first.focus();
  }

  function close() {
    drawer.classList.remove("is-open");
    document.body.classList.remove("no-scroll");
    if (lastFocused) lastFocused.focus();
    // Wait out the slide before hiding, so it does not vanish mid-motion.
    setTimeout(function () {
      if (!drawer.classList.contains("is-open")) drawer.hidden = true;
    }, 520);
  }

  openers.forEach(function (btn) {
    btn.addEventListener("click", open);
  });
  closers.forEach(function (btn) {
    btn.addEventListener("click", close);
  });

  document.addEventListener("keydown", function (e) {
    if (drawer.hidden) return;
    if (e.key === "Escape") return close();
    if (e.key !== "Tab") return;

    // Trap: the drawer covers the page, so tabbing must not wander behind it.
    var items = focusables();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
})();
